import { useEffect, useState, useCallback } from "react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Mail, Save, RotateCcw, Send, Monitor, Smartphone, Eye } from "lucide-react";

const inputCls = "h-11 rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";
const areaCls = "rounded-lg border-white/[0.1] bg-[#0A0A0A] text-white placeholder:text-neutral-600 focus-visible:border-[#E30613] focus-visible:ring-1 focus-visible:ring-[#E30613]";
const CAT_ORDER = ["Account", "Certificates", "Follow-Ups", "Credits", "Communications"];

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onChange(!checked); }} data-testid={testid}
      className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${checked ? "bg-[#E30613]" : "bg-white/10"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}

function EditField({ label, value, onChange, testid, placeholder, area, rows = 6 }) {
  return (
    <div className="space-y-2">
      <Label className="text-neutral-300">{label}</Label>
      {area ? (
        <Textarea rows={rows} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={areaCls} data-testid={testid} />
      ) : (
        <Input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={inputCls} data-testid={testid} />
      )}
    </div>
  );
}

export default function EmailTemplates() {
  const [list, setList] = useState([]);
  const [sender, setSender] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");
  const [device, setDevice] = useState("desktop");
  const [saving, setSaving] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("lrizi2002@gmail.com");
  const [testing, setTesting] = useState(false);

  const loadList = useCallback(() => {
    setLoading(true);
    api.get("/admin/email-templates")
      .then(({ data }) => { setList(data.templates); setSender(data.sender); if (!selected && data.templates.length) selectType(data.templates[0].type); })
      .catch(() => toast.error("Failed to load email templates"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { loadList(); }, [loadList]);

  const selectType = async (etype) => {
    setSelected(etype);
    setForm(null);
    setPreviewHtml("");
    try {
      const { data } = await api.get(`/admin/email-templates/${etype}`);
      setForm(data);
      setPreviewHtml(data.preview_html);
    } catch { toast.error("Failed to load template"); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const payload = (f) => ({
    subject: f.subject, header: f.header, subtitle: f.subtitle, body: f.body,
    cta_text: f.cta_text, footer: f.footer, signature: f.signature, is_active: f.is_active,
  });

  const refreshPreview = async (f = form) => {
    if (!f) return;
    setPreviewing(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${f.type}/preview`, payload(f));
      setPreviewHtml(data.preview_html);
    } catch { toast.error("Failed to render preview"); }
    finally { setPreviewing(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/admin/email-templates/${form.type}`, payload(form));
      setForm((f) => ({ ...f, ...data }));
      setList((l) => l.map((t) => (t.type === data.type ? { ...t, ...data } : t)));
      toast.success("Template saved");
      refreshPreview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to save"); }
    finally { setSaving(false); }
  };

  const resetDefault = async () => {
    try {
      const { data } = await api.post(`/admin/email-templates/${form.type}/reset`);
      setForm((f) => ({ ...f, ...data }));
      setList((l) => l.map((t) => (t.type === data.type ? { ...t, ...data } : t)));
      toast.success("Reset to default");
      const detail = await api.get(`/admin/email-templates/${data.type}`);
      setPreviewHtml(detail.data.preview_html);
    } catch { toast.error("Failed to reset"); }
  };

  const quickToggle = async (etype, is_active) => {
    setList((l) => l.map((t) => (t.type === etype ? { ...t, is_active } : t)));
    if (form?.type === etype) setForm((f) => ({ ...f, is_active }));
    try {
      await api.put(`/admin/email-templates/${etype}`, { is_active });
      toast.success(is_active ? "Email enabled" : "Email disabled");
    } catch { toast.error("Failed to update"); loadList(); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/admin/email-templates/${form.type}/test`, { to: testEmail });
      toast.success(data.message);
      setTestOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to send test"); }
    finally { setTesting(false); }
  };

  const grouped = CAT_ORDER.map((cat) => ({ cat, items: list.filter((t) => t.category === cat) })).filter((g) => g.items.length);

  return (
    <DashboardLayout title="Email Templates">
      <div className="mb-6">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#E30613]">Communications</p>
        <h2 className="mt-2 font-display text-2xl font-semibold tracking-tight text-white">Email Template Center</h2>
        <p className="mt-1 text-sm text-neutral-500">Customize, toggle, preview and test every SGBINOXE Care email. Sender: <span className="text-neutral-300">{sender}</span></p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
          {/* Left: list */}
          <div className="xl:col-span-3" data-testid="email-template-list">
            <div className="space-y-5">
              {grouped.map((g) => (
                <div key={g.cat}>
                  <p className="mb-2 px-1 text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-600">{g.cat}</p>
                  <div className="space-y-1.5">
                    {g.items.map((t) => (
                      <div key={t.type} role="button" tabIndex={0} onClick={() => selectType(t.type)}
                        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); selectType(t.type); } }}
                        data-testid={`email-type-${t.type}`}
                        className={`flex w-full cursor-pointer items-center justify-between gap-2 rounded-lg border px-3 py-2.5 text-left transition-colors ${selected === t.type ? "border-[#E30613]/40 bg-[#E30613]/[0.1]" : "border-white/[0.06] bg-[#141414] hover:bg-white/[0.04]"}`}>
                        <div className="flex items-center gap-2 min-w-0">
                          <Mail className={`h-3.5 w-3.5 shrink-0 ${t.is_active ? "text-[#FF6069]" : "text-neutral-600"}`} />
                          <span className={`truncate text-sm ${t.is_active ? "text-white" : "text-neutral-500"}`}>{t.label}</span>
                        </div>
                        <Toggle checked={t.is_active} onChange={(v) => quickToggle(t.type, v)} testid={`email-toggle-${t.type}`} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Middle: editor */}
          <div className="xl:col-span-5" data-testid="email-template-editor">
            {!form ? (
              <div className="flex items-center justify-center rounded-2xl border border-white/[0.06] bg-[#141414] py-20 text-neutral-500"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : (
              <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-6">
                <div className="mb-5 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-display text-base font-medium text-white">{form.label}</h3>
                    <p className="text-xs text-neutral-500">{form.is_active ? "Active — this email will be sent" : "Disabled — this email is turned off"}</p>
                  </div>
                  <Toggle checked={form.is_active} onChange={(v) => set("is_active")(v)} testid="editor-active-toggle" />
                </div>

                {form.variables?.length > 0 && (
                  <div className="mb-5">
                    <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-neutral-600">Available Variables</p>
                    <div className="flex flex-wrap gap-1.5">
                      {form.variables.map((v) => (
                        <span key={v} className="rounded-md border border-white/[0.08] bg-[#0A0A0A] px-2 py-1 font-mono text-[11px] text-neutral-400">{`{${v}}`}</span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-4">
                  <EditField label="Subject Line" value={form.subject} onChange={set("subject")} testid="field-subject" />
                  <EditField label="Header Title" value={form.header} onChange={set("header")} testid="field-header" />
                  <EditField label="Subtitle" value={form.subtitle} onChange={set("subtitle")} placeholder="Optional" testid="field-subtitle" />
                  <EditField label="Body" value={form.body} onChange={set("body")} area rows={7} testid="field-body" />
                  <EditField label="CTA Button Text" value={form.cta_text} onChange={set("cta_text")} placeholder="Leave blank to hide the button" testid="field-cta" />
                  <EditField label="Signature" value={form.signature} onChange={set("signature")} area rows={2} placeholder="Optional signature below the body" testid="field-signature" />
                  <EditField label="Footer Text" value={form.footer} onChange={set("footer")} area rows={2} placeholder="Leave blank to use the standard SGBINOXE Care footer" testid="field-footer" />
                </div>

                <div className="mt-6 flex flex-wrap gap-2">
                  <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="save-template-btn">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save
                  </button>
                  <button onClick={() => refreshPreview()} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm text-neutral-300 hover:bg-white/[0.05]" data-testid="refresh-preview-btn">
                    <Eye className="h-4 w-4" /> Update Preview
                  </button>
                  <button onClick={() => setTestOpen(true)} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm text-neutral-300 hover:bg-white/[0.05]" data-testid="open-test-btn">
                    <Send className="h-4 w-4" /> Send Test
                  </button>
                  <button onClick={resetDefault} className="inline-flex items-center gap-2 rounded-lg border border-white/[0.1] px-4 py-2.5 text-sm text-neutral-400 hover:bg-white/[0.05]" data-testid="reset-template-btn">
                    <RotateCcw className="h-4 w-4" /> Reset
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Right: preview */}
          <div className="xl:col-span-4" data-testid="email-template-preview">
            <div className="rounded-2xl border border-white/[0.06] bg-[#141414] p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="font-display text-sm font-medium text-white">Preview {previewing && <Loader2 className="ml-1 inline h-3 w-3 animate-spin" />}</h3>
                <div className="flex gap-1 rounded-lg border border-white/[0.08] p-1">
                  <button onClick={() => setDevice("desktop")} className={`rounded-md p-1.5 ${device === "desktop" ? "bg-[#E30613] text-white" : "text-neutral-400"}`} data-testid="preview-desktop-btn"><Monitor className="h-4 w-4" /></button>
                  <button onClick={() => setDevice("mobile")} className={`rounded-md p-1.5 ${device === "mobile" ? "bg-[#E30613] text-white" : "text-neutral-400"}`} data-testid="preview-mobile-btn"><Smartphone className="h-4 w-4" /></button>
                </div>
              </div>
              <div className="flex justify-center overflow-auto rounded-xl bg-[#050505] p-3" style={{ maxHeight: "70vh" }}>
                <iframe title="email-preview" srcDoc={previewHtml} data-testid="preview-iframe"
                  style={{ width: device === "mobile" ? 380 : "100%", height: "68vh", border: "none", background: "#050505" }} />
              </div>
            </div>
          </div>
        </div>
      )}

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-md border-white/[0.08] bg-[#141414] text-white" data-testid="test-email-modal">
          <DialogHeader>
            <DialogTitle>Send Test Email</DialogTitle>
            <DialogDescription className="text-neutral-500">A sample of &ldquo;{form?.label}&rdquo; will be sent using placeholder data.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-neutral-300">Recipient Email</Label>
            <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} className={inputCls} data-testid="test-email-input" />
          </div>
          <DialogFooter>
            <button onClick={() => setTestOpen(false)} className="rounded-lg border border-white/[0.1] px-4 py-2 text-sm text-neutral-300 hover:bg-white/[0.05]">Cancel</button>
            <button onClick={sendTest} disabled={testing || !testEmail} className="inline-flex items-center gap-2 rounded-lg bg-[#E30613] px-5 py-2 text-sm font-medium text-white hover:bg-[#FF1C2A] disabled:opacity-60" data-testid="send-test-btn">
              {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send Test
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
